import React from "react";
import { cn } from "@/lib/utils";

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "default" | "destructive" | "outline" | "secondary" | "ghost" | "link";
  size?: "default" | "sm" | "lg" | "icon";
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant = "default", size = "default", ...props }, ref) => {
    const baseClasses = [
      "inline-flex items-center justify-center whitespace-nowrap rounded-md text-sm font-medium",
      "ring-offset-2 transition-colors focus-visible:outline-none focus-visible:ring-2",
      "focus-visible:ring-slate-300 disabled:pointer-events-none disabled:opacity-50",
    ];

    const variantClasses = {
      default: "bg-slate-50 text-slate-900 hover:bg-slate-50/90",
      destructive: "bg-red-900 text-slate-50 hover:bg-red-900/90",
      outline: "border border-slate-800 bg-slate-950 hover:bg-slate-800 hover:text-slate-50",
      secondary: "bg-slate-800 text-slate-50 hover:bg-slate-800/80",
      ghost: "hover:bg-slate-800 hover:text-slate-50",
      link: "text-slate-50 underline-offset-4 hover:underline",
    };

    const sizeClasses = {
      default: "h-10 px-4 py-2",
      sm: "h-9 rounded-md px-3",
      lg: "h-11 rounded-md px-8",
      icon: "h-10 w-10",
    };

    return (
      <button
        className={cn(
          baseClasses,
          variantClasses[variant],
          sizeClasses[size],
          className
        )}
        ref={ref}
        {...props}
      />
    );
  }
);

Button.displayName = "Button";

export { Button };